import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';
import { JobDropdown, ProfileModel } from '@ups/xplat/api/dto';

const USER_DEFAULTS = '/api/userDefault/{:auth0UserId}';
const GET_JOBS = '/api/jobs/dropdown';
const GET_DEFAULT_JOB = '/api/jobs/default';
const FAVORITE_JOB = '/api/jobs/favorite';

@Injectable({ providedIn: 'root' })
export class JobSearchService {
  private defaultJobSubject = new BehaviorSubject<JobDropdown | null>(null);
  public defaultJob = this.defaultJobSubject.asObservable();

  /** Bumps when a new `saveDefaultJob` starts so stale in-flight saves do not overwrite `defaultJobSubject`. */
  private defaultJobSaveGeneration = 0;

  private securityClient;
  private spartaClient;
  private profile: ProfileModel;

  constructor(private clientFactory: MyHttpClientFactory) {
    this.securityClient = this.clientFactory.createHttpClient(
      environment.urls.securityAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
    const urlBase = environment.urls.spartaAPI;
    this.spartaClient = this.clientFactory.createHttpClient(
      urlBase,
      true,
      ResponseCasingEnum.PascalCase
    );
    this.profile = JSON.parse(localStorage.getItem('profile')) as ProfileModel;
    // this.getDefaultJob(this.profile.auth0UserId, this.profile.hrRef);
  }

  public getUserDefaults(auth0UserId: string): Promise<unknown> {
    const url = USER_DEFAULTS.split('{:auth0UserId}').join(auth0UserId);
    return firstValueFrom(this.securityClient.get(url));
  }

  /**
   * Persists the user's default job to Security, then refreshes from Sparta and publishes to `defaultJobSubject`.
   * @param jobID Viewpoint-style job id string stored in user defaults.
   * @param optimisticJob When set, published to `defaultJobSubject` immediately so other routes/components
   *        see the new default before GET/PUT complete (avoids stale replay while save is in flight).
   */
  public saveDefaultJob(
    jobID: string,
    optimisticJob?: JobDropdown | null
  ): Promise<void> {
    if (!jobID) {
      return Promise.resolve();
    }

    if (optimisticJob) {
      this.defaultJobSubject.next(optimisticJob);
    }

    const generation = ++this.defaultJobSaveGeneration;

    const run = async (): Promise<void> => {
      const def = (await this.getUserDefaults(this.profile.auth0UserId)) as {
        defaultJob?: string;
      };
      if (generation !== this.defaultJobSaveGeneration) {
        return;
      }
      def.defaultJob = jobID;
      const url = USER_DEFAULTS.split('{:auth0UserId}').join(
        this.profile.auth0UserId
      );
      await firstValueFrom(this.securityClient.put(url, def));
      if (generation !== this.defaultJobSaveGeneration) {
        return;
      }
      const defaultJob: JobDropdown = await this._getDefaultJobCall(
        this.profile.auth0UserId,
        null
      );
      if (generation !== this.defaultJobSaveGeneration) {
        return;
      }
      this.defaultJobSubject.next(defaultJob);
    };

    return run();
  }

  public addOrRemoveFavoriteJob(jobID: string) {
    const url = FAVORITE_JOB;
    this.spartaClient
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .post(url, { UserID: this.profile.auth0UserId, JobID: jobID })
      .toPromise();
  }

  public async getDefaultJob(
    auth0UserId: string,
    hrRef: string = null
  ): Promise<JobDropdown | null> {
    const defaultValue = await firstValueFrom(this.defaultJobSubject);
    if (defaultValue === null) {
      const response: JobDropdown = await this._getDefaultJobCall(
        auth0UserId,
        hrRef
      );
      this.defaultJobSubject.next(response);
      return response;
    }
    return defaultValue;
  }

  public searchJobs(
    search: string,
    companyIDs: number[],
    hrRef: string = null,
    active: boolean = true,
    includeTemporaryForemanJobs = false,
    parentFacilityIDs: number[] = []
  ): Promise<JobDropdown[]> {
    let url = `${GET_JOBS}/?searchString=${encodeURIComponent(search)}&status=${active}`;

    if (companyIDs?.length) {
      companyIDs.forEach((c) => {
        url += `&vpCompanyIDs=${c}`;
      });
    }

    if (parentFacilityIDs?.length) {
      parentFacilityIDs.forEach((id) => {
        url += `&parentFacilityIDs=${id}`;
      });
    }

    url += `&hrRef=${hrRef || this.profile.hrRef}`;
    if (includeTemporaryForemanJobs) {
      url += '&includeTemporaryForemanJobs=true';
    }

    return this.spartaClient.get(url).toPromise();
  }

  private async _getDefaultJobCall(
    auth0UserId: string,
    hrRef: string = null
  ): Promise<JobDropdown> {
    let url = GET_DEFAULT_JOB;

    if (auth0UserId) {
      url += '/' + auth0UserId;
    }

    url = url + `?hrRef=${hrRef || this.profile.hrRef}`;

    return (await firstValueFrom(this.spartaClient.get(url))) as JobDropdown;
  }
}
